// Home.jsx — Ai hub (idle Capture + session chat)

import React, { useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useUser } from 'context/UserContext';
import { useBase } from 'context/BaseContext';
import usePeekPanelController from 'node/hooks/usePeekPanelController';
import useNodeOpenIntent from 'views/hooks/useNodeOpenIntent';

// import useSidebarBucketsData from 'main/sidebar/buckets/hooks/useSidebarBucketsData';
import useBaseChannels from 'channels/hooks/useBaseChannels';
import Section from 'components/section/Section';
// import CaptureHome from 'capture/components/CaptureHome';
import HomeActivityFeed from 'home/activity/HomeActivityFeed';
import HomePinnedDashboard from 'home/HomePinnedDashboard';
import PeekPanel from 'node/peek/PeekPanel';
import AiHomeSession from 'spaces/components/AiHomeSession';
import { buildAiHomePath } from 'spaces/utils/buildAiHomePath';
import {
  buildAiFeedChannelKey,
  isAiChannelKeyForUser,
} from 'spaces/utils/aiChannelKeys';
import { setLastActiveSessionKey } from 'spaces/utils/aiSessionPrefs';

import './Home.css';
// import 'capture/components/CaptureHome.css';

/**
 * Home
 *
 * Primary Ai hub: idle Capture surface, or an active AI session.
 */
const Home = () => {
  const { user } = useUser();
  const { activeBase } = useBase();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const activeBaseKey = typeof activeBase?.key === 'string'
    ? activeBase.key.trim()
    : '';
  const userKey = String(user?.key || '').trim();

  const sessionKey = String(searchParams.get('session') || '').trim();
  const hasOwnedSession = Boolean(
    sessionKey
    && activeBaseKey
    && userKey
    && isAiChannelKeyForUser(sessionKey, activeBaseKey, userKey),
  );

  // Prototype: CaptureHome is commented out on idle Home.
  // const { databases } = useSidebarBucketsData({
  //   baseKey: activeBaseKey,
  // });

  const {
    unreadByChannel,
  } = useBaseChannels({
    baseKey: activeBaseKey,
    user,
  });

  const { openNode } = useNodeOpenIntent();

  const {
    isPeekOpen,
    closePeek,
    peekLocalNode,
    isPeekLoading,
    peekError,
    canPeekGoBack,
    handlePeekBack,
    handlePeekTitleSaved,
    handlePeekTagsSaved,
    handlePeekLocationSaved,
    handlePeekSubtitleSaved,
    handlePeekCoverSaved,
    handlePeekNodeChange,
  } = usePeekPanelController({ user });

  const feedChannelKey = useMemo(
    () => buildAiFeedChannelKey(activeBaseKey, userKey),
    [activeBaseKey, userKey],
  );

  const feedUnread = Boolean(feedChannelKey && unreadByChannel?.[feedChannelKey]);

  const pinnedDashboardKey = useMemo(() => {
    if (!activeBaseKey) return '';
    const pinnedMap = user?.data_profile?.pinnedDashboardByBase;
    if (!pinnedMap || typeof pinnedMap !== 'object') return '';
    const pinnedKey = pinnedMap[activeBaseKey];
    return typeof pinnedKey === 'string' ? pinnedKey.trim() : '';
  }, [activeBaseKey, user?.data_profile?.pinnedDashboardByBase]);

  // const [homeVisitKey] = useState(() => Date.now());
  const hasPinnedDashboard = Boolean(pinnedDashboardKey);

  const handleBackToIdle = useCallback(() => {
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  const handleOpenSession = useCallback((channelKey) => {
    const key = String(channelKey || '').trim();
    if (!key) return;
    setLastActiveSessionKey({
      baseKey: activeBaseKey,
      userKey,
      sessionKey: key,
    });
    navigate(buildAiHomePath({ channelKey: key }));
  }, [activeBaseKey, navigate, userKey]);

  const handleOpenUpdates = useCallback(() => {
    if (!feedChannelKey) return;
    handleOpenSession(feedChannelKey);
  }, [feedChannelKey, handleOpenSession]);

  // ----- RENDER: active member session ----- //

  if (hasOwnedSession) {
    return (
      <Section gap="0px" padding={0}>
        <div className="home-layout home-layout--session">
          <AiHomeSession
            sessionKey={sessionKey}
            onBackToIdle={handleBackToIdle}
          />
        </div>
      </Section>
    );
  }

  // ----- RENDER: idle hub ----- //

  return (
    <Section gap="0px" padding={0}>
      <div className="home-layout">

        {/* 
        {feedUnread ? (
          <button
            type="button"
            className="home-ai-updates-nudge"
            onClick={handleOpenUpdates}
          >
            <span className="home-ai-updates-nudge__dot" aria-hidden />
            New Updates
          </button>
        ) : null}
        */}

        {/* Prototype: personal activity feed replaces the Home AI prompt.
        <CaptureHome
          compact={hasPinnedDashboard}
          databases={databases}
          homeVisitKey={homeVisitKey}
        />
        */}

        <HomeActivityFeed
          baseKey={activeBaseKey}
          userKey={userKey}
          onOpenNode={openNode}
        />

        {hasPinnedDashboard ? (
          <>
            <hr className="home-pinned-dashboard__divider" />
            <HomePinnedDashboard dashboardKey={pinnedDashboardKey} />
          </>
        ) : null}
      </div>

      <PeekPanel
        isOpen={isPeekOpen}
        node={peekLocalNode}
        isLoading={isPeekLoading}
        error={peekError}
        canGoBack={canPeekGoBack}
        onBack={handlePeekBack}
        onClose={closePeek}
        onTitleSaved={handlePeekTitleSaved}
        onTagsSaved={handlePeekTagsSaved}
        onLocationSaved={handlePeekLocationSaved}
        onSubtitleSaved={handlePeekSubtitleSaved}
        onCoverSaved={handlePeekCoverSaved}
        onNodeChange={handlePeekNodeChange}
      />
    </Section>
  );
};

Home.displayName = 'Home';

export default Home;
